/* Run status, and — the part that actually matters — *why* a run is not moving.

   A status of "running" that has not moved in four minutes is indistinguishable
   from a status of "running" that is mid-inference, and the user cannot tell
   which one is burning money. Every stalled state therefore carries a reason
   and, where one exists, the action that unblocks it. */

import type { Run } from "@/shared/types";
import type { BlockedReasonId, BlockedReasonMeta, RunStatusId, RunStatusMeta, Span } from "../types";

export const RUN_STATUS: Record<RunStatusId, RunStatusMeta> = {
  queued: { label: "Queued", tone: "neutral", terminal: false },
  running: { label: "Running", tone: "info", terminal: false },
  blocked: { label: "Blocked", tone: "warn", terminal: false },
  attention: { label: "Needs attention", tone: "warn", terminal: false },
  success: { label: "Completed", tone: "success", terminal: true },
  error: { label: "Failed", tone: "danger", terminal: true },
  halted: { label: "Halted", tone: "danger", terminal: true },
};

/* Why a non-terminal run is not progressing. `action` is what the user can do
   about it; null means only the agent or the clock can move it forward. */
export const BLOCKED_REASON: Record<BlockedReasonId, BlockedReasonMeta> = {
  approval: { label: "Waiting for approval", action: "Review" },
  input: { label: "Waiting for your reply", action: "Reply" },
  permission: { label: "Waiting for permission", action: "Grant" },
  budget: { label: "Budget cap reached", action: "Raise cap" },
  rate_limit: { label: "Rate limited", action: null },
  guardrail: { label: "Stopped by policy", action: "Review policy" },
  upstream: { label: "Waiting on an upstream agent", action: null },
  inference: { label: "Generating", action: null },
  tool: { label: "Running a tool", action: null },
};

// Both lookups take any string: a run arrives with whatever status the store
// wrote, and an unknown one falls back rather than throwing.
export const statusMeta = (s: string | undefined): RunStatusMeta =>
  RUN_STATUS[s as RunStatusId] || RUN_STATUS.queued;
export const blockedMeta = (r: string | undefined): BlockedReasonMeta | null =>
  (r ? BLOCKED_REASON[r as BlockedReasonId] || null : null);

/* The live one-liner shown on a run row and in the header — "Running" tells the
   user nothing they did not already know from the spinner, so say what it is
   running. Falls back to the status label for terminal runs. */
export function liveLabel(run: Run, currentSpan: Span | null): string {
  if (run.status !== "running") {
    const b = blockedMeta(run.blockedReason);
    return b ? b.label : statusMeta(run.status).label;
  }
  if (run.blockedReason) return blockedMeta(run.blockedReason)?.label || "Running";
  if (!currentSpan) return "Starting up";
  if (currentSpan.kind === "llm") return `Generating · ${currentSpan.label}`;
  if (currentSpan.kind === "tool") return `Running ${currentSpan.name}`;
  return currentSpan.label || "Working";
}

/* Runs that ended cleanly but left something for a human are "attention", not
   "success" — collapsing the two is how an unreviewed approval goes unnoticed. */
export function effectiveStatus(run: Run): RunStatusId {
  if (run.status === "running" && run.blockedReason === "approval") return "blocked";
  return run.status;
}
